// What a sign-in reconciliation's answer means for the UI language — the decision alone, as a pure
// function (ADR-0002 entry rule A), because its interesting cases are about TIMING and a spec samples
// one of them at a time.
//
// `POST /api/profile/language/reconcile` answers the question "what language should this person be
// addressed in?" from the language the SPA was showing WHEN THE CALL WAS MADE. A round trip is long
// enough for the person to answer it themselves in the meantime — the switcher sits in the header of the
// very first screen a session renders — and the answer that then arrives is about a language they have
// already left. Applying it puts the UI back where they moved away from (task c4b87c3b: in a loaded
// parallel E2E run the reconciliation outlived a switch to English, and the restored page came back
// Finnish). So the answer is weighed against what the person is reading NOW, not against nothing.
//
// That comparison alone was not enough, and the second half of the rule is {@link noteLanguageStated}.
// It covers the OTHER order, which ADR-0018 §6.2 opened by making this reconciliation wait for
// `GET /api/auth/memberships` (task ef4652bb): the call now sets out a round trip AFTER the load, so the
// person can have used the switcher BEFORE it goes out. `asked` is then already their new language, the
// comparison above sees no change at all — and the row the server reads is still the old one, because the
// switcher's own `PUT /api/profile/language` is a separate request that has not committed yet. The answer
// is the language they just left, it looks current, and it is applied. Measured in a full-stack run
// (task 84dd2eef): the two requests left the browser 33 ms apart, and four of five runs of
// change-observation-sharing-ui's fi/en sweep went back to Finnish mid-pass.

import type { Language } from "../i18n";

/**
 * Whether the person has STATED a UI language in this document — i.e. used the switcher since the page
 * loaded. Module state rather than an argument, because the two halves of this are in different places:
 * the statement is made in the header (components/LanguageSwitcher.tsx) and the question is asked above
 * the router (profile/PreferredLanguageSync.tsx), and nothing between them carries it.
 *
 * Scoped to the document on purpose. A reload is a new page with a new answer to settle, and the row the
 * switcher wrote is what it settles against — which by then is the person's own statement.
 */
let statedHere = false;

/** Record that the person has chosen a language HERE. Called by the switcher, for every switch. */
export function noteLanguageStated(): void {
  statedHere = true;
}

/** Whether {@link noteLanguageStated} has been called in this document. */
export function languageWasStated(): boolean {
  return statedHere;
}

/** The reconciliation as this decision sees it. */
export interface ReconciledLanguage {
  /** The language the SPA was showing when the call was made (what the seed, if any, was taken from). */
  asked: Language | null;
  /** The language the SPA is showing now, when the answer arrived. */
  current: Language | null;
  /** The preference the server settled on — `null` when the row still has none. */
  settled: Language | null;
  /**
   * Whether the person has used the switcher in this document, at any point — before this call went out
   * or while it was in flight. {@link languageWasStated} is the reading of it.
   */
  stated: boolean;
}

/**
 * The language the UI must switch to, or `null` to leave it alone. Leaves it alone when:
 *
 *   - the row has no preference (nothing was settled, so there is nothing to follow);
 *   - the person has stated a language in this document — their choice is the LATER statement whichever
 *     side of this call they made it, and the switcher has written it to the row, so this answer is
 *     either about the row before that write or it agrees with it;
 *   - the person changed the language while the call was in flight (the same rule, seen in the one window
 *     where the two languages differ on their own);
 *   - the settled language is the one already on screen.
 */
export function languageToApply({
  asked,
  current,
  settled,
  stated,
}: ReconciledLanguage): Language | null {
  if (settled === null) return null;
  if (stated) return null;
  if (current !== asked) return null;
  if (settled === current) return null;
  return settled;
}
